import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import cn from 'comma-number'
import differenceInDays from 'date-fns/differenceInDays'
import differenceInCalendarYears from 'date-fns/differenceInCalendarYears'
import Flex from 'Components/Flex'
import {
  checkListingLocked,
} from 'Utils/listingUtils'
import { createPhotoPaths } from 'Utils/photoUtils'

import InfoHolderLocked from 'Components/ListingPreview/components/InfoHolderLocked'
import AppLink from 'Components/AppLink'
import BlurCont from 'Layout/BlurCont'
import ImgPreview from 'Components/ListingPreview/components/ImgPreview'
import ListingRemovalMessage from 'Components/ListingRemovalMessage'
import ListingPreviewLockedText from 'Components/ListingPreviewLockedText'
import { ListingV2 } from 'Pages'
import FavouritesWrapper from 'Components/Favourites/FavouritesWrapper'

import SvgIcon from 'Components/SvgIcon'
import { checkIsArchive } from '../../utils'
import {
  _Title,
  _CardContent,
  _ImageContainer,
  _ListingBanner,
  _ListingDetail,
  _Container,
  _ContainerDiv,
  _Price,
  _InfoHolderLockContainer,
  _ListingSpec,
  _ListingRemovalMessageContainer,
  _FeaturedListingIndicator,
} from './styles'

const BREAK_POINT_320 = 320
const BREAK_POINT_375 = 375
const GOOGLE_EVENT_CATEGORY = 'Map Listing Popup'


const ListingListViewContent = ({
  listing,
  userIsAuthentificated,
  userIsOneOfAgentAdminSupport,
  googleAnalyticsEvent = () => {},
  offer,
  isBc,
  winWidth,
  openLogin = () => {},
  onMouseLeave = () => {},
  onMouseEnter = () => {},
  signupOnImage = () => {},
  signupOnInfo = () => {},
  openEmailVerifyModal,
  requireTermsConditionAgreement,
  openTermsConditionModal,
  listViewStyle,
  requiredEmailVerification,
  termsConditionRequiredId,
  multiListings,
}) => {
  const firstPhotoObj = createPhotoPaths({
    photoBaseUrl: listing.photo_base_url,
    mlsNumber: listing.mls_number,
    photoIndex: 1,
    photoVersion: listing.photo_version,
    photoSource: listing.photo_source,
  })
  const isArchived = checkIsArchive(listing.status)
  const showLockedMode = checkListingLocked({
    listing,
    isArchived,
    userIsAuthentificated,
    requiredEmailVerification,
    termsConditionRequiredId,
    requireTermsConditionAgreement,
  })

  const WrapperComp = showLockedMode ? _ContainerDiv : _Container
  const endDateUnix = listing.end_date_unix
  const entryDateUnix = listing.entry_date_unix
  const closedPrice = listing.closed_price
  const listedDays = entryDateUnix
    ? differenceInDays(new Date(), entryDateUnix * 1000)
    : null
  const archivedYears = endDateUnix ?
    differenceInCalendarYears(new Date(), endDateUnix * 1000) :
    null
  const hiddenFields = userIsOneOfAgentAdminSupport ? null : listing.hidden_fields
  const listingHiddenFields = hiddenFields || {}

  const onImageClick = () => {
    if(requireTermsConditionAgreement) {
      openTermsConditionModal()
    } else if(requiredEmailVerification) {
      openEmailVerifyModal()
    } else if(showLockedMode) {
      signupOnImage()
    }
  }

  return (
    <WrapperComp
      openNewTab
      listViewStyle={listViewStyle}
      data-test-id="listingListViewCard"
      to={'/' + listing.url}
      onClick={() => googleAnalyticsEvent({
        category: GOOGLE_EVENT_CATEGORY,
        action: `[Navigate to listing]${listing?.featured ? '[Featured]' : ''}`,
      })}
      lookupLinkData={{
        component: 'Listing',
        id: listing.id,
        pageData: listing,
      }}
      onMouseLeave={onMouseLeave}
      onMouseEnter={onMouseEnter}
      mobileModalChildren={
        // eslint-disable-next-line react/no-unstable-nested-components
        ({ closeModalPage }) => (
          <ListingV2
            listingId={listing.id}
            modalListingData={listing}
            closeModalPage={closeModalPage}
            isArchived={isArchived}
          />
        )
      }
      mobileModalKey={`listingModal-${listing.id}`}
    >
      <_CardContent featured={listing?.featured} multiListings={multiListings}>
        <_ImageContainer
          data-test-id="listViewContentImageContainer"
          onClick={onImageClick}
        >
          {
            !showLockedMode && (
              <_ListingBanner
                isArchived={isArchived}
                listedDays={listedDays}
                archivedYears={archivedYears}
                endDateUnix={endDateUnix}
                bannerId={userIsAuthentificated}
                offer={offer}
                status={listing.status}
                featured={listing.featured}
                forListView
                multiListings={multiListings}
              />
            )
          }
          <ImgPreview
            title={listing.title}
            imageUrl={
              !listingHiddenFields.photos && firstPhotoObj
                ? firstPhotoObj.imgSmall
                : ''
            }
            alt={listing.title}
            locked={showLockedMode}
            lockedText={
              <ListingPreviewLockedText
                showShortText
                source={listing?.source}
                requireTermsConditionAgreement={requireTermsConditionAgreement}
                requiredEmailVerification={requiredEmailVerification}
              />
            }
          />
          {
            listing?.featured ?
              <_FeaturedListingIndicator>
                <SvgIcon name="#icon-star" width={10} height={10} />
                <span>Featured</span>
              </_FeaturedListingIndicator>
              : <> </>
          }
        </_ImageContainer>
        <_ListingDetail isBc={isBc}>
          <Flex justify="between" align="center">
            {
              closedPrice ? (
                <_Price>
                  {
                    listingHiddenFields.prices
                      ? 'Price not available'
                      : (
                        <Fragment>
                          {'$' + cn(closedPrice)}{' '}
                          <BlurCont blur={isArchived && !userIsAuthentificated}>
                            <span> {'$' + cn(listing.asking_price)}</span>
                          </BlurCont>
                        </Fragment>
                      )
                  }


                </_Price>
              ) : (
                <_Price blur={showLockedMode}>
                  {'$' + cn(listing.asking_price)}
                </_Price>
              )
            }
            <FavouritesWrapper
              item={listing}
              gaCategory={GOOGLE_EVENT_CATEGORY}
              isArchived={isArchived}
              isMapPopup
              gaSource="Listing List View Card"
            />
          </Flex>
          <BlurCont blur={(isArchived && !userIsAuthentificated) || requiredEmailVerification}>
            <_Title>{listing.title}</_Title>
          </BlurCont>
          {
            (showLockedMode) ? (
              <_InfoHolderLockContainer>
                <InfoHolderLocked
                  onLogin={openLogin}
                  onRegister={signupOnInfo}
                  requiredEmailVerification={requiredEmailVerification}
                  openEmailVerifyModal={openEmailVerifyModal}
                  source={listing?.source}
                  requireTermsConditionAgreement={requireTermsConditionAgreement}
                  onTermsConditionClick={openTermsConditionModal}
                />
              </_InfoHolderLockContainer>
            ) : (
              !listingHiddenFields.listing_info &&
              <_ListingSpec
                userIsAuthentificated={userIsAuthentificated}
                listing={listing}
                offer={offer || 'Sale'}
                smallerThan320={winWidth <= BREAK_POINT_320}
                smallerThan375={winWidth <= BREAK_POINT_375}
                isBc={isBc}
              />
            )
          }
          {
            hiddenFields && (
              <_ListingRemovalMessageContainer
                hiddenFields={hiddenFields}
                userIsAuthentificated={userIsAuthentificated}
              >
                <AppLink
                  openNewTab
                  to={'/' + listing.url}
                  alt="navigate to listing"
                  onClick={() => {
                    googleAnalyticsEvent({
                      category: GOOGLE_EVENT_CATEGORY,
                      action: `[Navigate to listing]${listing?.featured ? '[Featured]' : ''}`,
                    })
                  }}
                  lookupLinkData={{
                    component: 'Listing',
                    id: listing.id,
                    pageData: listing,
                  }}
                >
                  <ListingRemovalMessage
                    showShortMessage
                    hiddenFields={hiddenFields}
                    hideContactAgentText
                  />
                </AppLink>
              </_ListingRemovalMessageContainer>
            )
          }

        </_ListingDetail>
      </_CardContent>
    </WrapperComp>
  )
}

ListingListViewContent.propTypes = {
  winWidth: PropTypes.number,
  markerAddressData: PropTypes.shape({}),
  userIsAuthentificated: PropTypes.bool,
  isBc: PropTypes.bool,
  offer: PropTypes.string,
  googleAnalyticsEvent: PropTypes.func,
  signupOnImage: PropTypes.func,
  signupOnInfo: PropTypes.func,
  openLogin: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onMouseEnter: PropTypes.func,
  openEmailVerifyModal: PropTypes.func,
  requiredEmailVerification: PropTypes.bool,
  userIsOneOfAgentAdminSupport: PropTypes.bool,
  listViewStyle: PropTypes.bool,
  requireTermsConditionAgreement: PropTypes.bool,
  openTermsConditionModal: PropTypes.func,
  termsConditionRequiredId: PropTypes.number,
  multiListings: PropTypes.bool,
  listing: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    url: PropTypes.string,
    brokerage: PropTypes.string,
    mls_number: PropTypes.string,
    created_at: PropTypes.string,
    status: PropTypes.string,
    featured: PropTypes.bool,
    asking_price: PropTypes.number,
    end_date_unix: PropTypes.number,
    closed_price: PropTypes.number,
    advertise_permission: PropTypes.number,
    photo_version: PropTypes.number,
    entry_date_unix: PropTypes.number,
    source: PropTypes.string,
    property_type: PropTypes.string,
    hidden_fields: PropTypes.shape({
      photos: PropTypes.bool,
      prices: PropTypes.bool,
      listing_info: PropTypes.bool,
    }),
    photo_base_url: PropTypes.string,
    photo_source: PropTypes.string,
  }),
}

export default ListingListViewContent
